import { useMemo, useState } from 'react';

import { COMPENSATION_TABLE_PAGE_SIZE } from '../constants';
import { filterLines } from '../utils/filterLines';
import { sortLines } from '../utils/sortLines';

import type { CompensationLineSortKey, SortDirection } from '../utils/sortLines';
import type { CompensationLine } from '@/types/compensation';

export interface AdjustmentsSort {
    key: CompensationLineSortKey;
    direction: SortDirection;
}

export interface UseAdjustmentsTableParams {
    lines: CompensationLine[];
    language: string;
}

export interface UseAdjustmentsTableResult {
    search: string;
    setSearch: (search: string) => void;
    /** `null` until a column header is clicked: the backend's own order (best scores first) is the
     * meaningful default, so the table does not impose one of its own. */
    sort: AdjustmentsSort | null;
    toggleSort: (key: CompensationLineSortKey) => void;
    page: number;
    setPage: (page: number) => void;
    totalPages: number;
    /** How many lines the search kept, across every page. */
    matchCount: number;
    pageLines: CompensationLine[];
}

/** Search, sort and pagination of the adjustments table. Purely a view concern: it never changes a
 * line, only which of them the current page shows. */
export const useAdjustmentsTable = ({
    lines,
    language,
}: UseAdjustmentsTableParams): UseAdjustmentsTableResult => {
    const [search, setSearchState] = useState('');
    const [sort, setSort] = useState<AdjustmentsSort | null>(null);
    const [page, setPage] = useState(1);

    const visibleLines = useMemo(() => {
        const matching = filterLines({ lines, search });
        return sort
            ? sortLines({ lines: matching, sortKey: sort.key, direction: sort.direction, language })
            : matching;
    }, [lines, search, sort, language]);

    const totalPages = Math.max(1, Math.ceil(visibleLines.length / COMPENSATION_TABLE_PAGE_SIZE));
    // Clamped rather than reset: removing the last line of the last page should show the new last
    // page, not send the reader back to the first one.
    const currentPage = Math.min(page, totalPages);

    const pageLines = useMemo(
        () =>
            visibleLines.slice(
                (currentPage - 1) * COMPENSATION_TABLE_PAGE_SIZE,
                currentPage * COMPENSATION_TABLE_PAGE_SIZE,
            ),
        [visibleLines, currentPage],
    );

    // Narrowing the list can only move a reader further from what they were looking at, so both
    // search and sort send them back to the first page.
    const setSearch = (value: string) => {
        setSearchState(value);
        setPage(1);
    };

    // Clicking the column already sorted on turns it around; clicking any other one starts it
    // ascending, which is what a reader expects of a column they have not touched yet.
    const toggleSort = (key: CompensationLineSortKey) => {
        setSort((current) => {
            if (current?.key !== key) {
                return { key, direction: 'asc' };
            }

            return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
        });
        setPage(1);
    };

    return {
        search,
        setSearch,
        sort,
        toggleSort,
        page: currentPage,
        setPage,
        totalPages,
        matchCount: visibleLines.length,
        pageLines,
    };
};
